// FRAML Customer Aggregate Detail reducer
// FLAT (non-keyed) slice for the currently-viewed customer. Each section:
// { data, isLoading, error }. Sagas use takeLatest so a superseded customer's
// fetch is cancelled before it can overwrite this shared slice.
// Source IDs: 6=AML, 25+29=Fraud (alerts split by domain)

import {
  FETCH_FRAML_CUSTOMER_AGGREGATE,
  RESOLVE_FRAML_ALERT,
  RESOLVE_FRAML_ALERT_SUCCESS,
  RESOLVE_FRAML_ALERT_FAIL,
  FETCH_FRAML_CUSTOMER_AGGREGATE_SUCCESS,
  FETCH_FRAML_CUSTOMER_AGGREGATE_FAIL,
  FETCH_FRAML_CUSTOMER_SCORE_HISTORY,
  FETCH_FRAML_CUSTOMER_SCORE_HISTORY_SUCCESS,
  FETCH_FRAML_CUSTOMER_SCORE_HISTORY_FAIL,
  FETCH_FRAML_CUSTOMER_ALERTS,
  FETCH_FRAML_CUSTOMER_ALERTS_SUCCESS,
  FETCH_FRAML_CUSTOMER_ALERTS_FAIL,
  FETCH_FRAML_CUSTOMER_RISK_FACTORS,
  FETCH_FRAML_CUSTOMER_RISK_FACTORS_SUCCESS,
  FETCH_FRAML_CUSTOMER_RISK_FACTORS_FAIL,
  FETCH_FRAML_CUSTOMER_DETAILS,
  FETCH_FRAML_CUSTOMER_DETAILS_SUCCESS,
  FETCH_FRAML_CUSTOMER_DETAILS_FAIL,
  FETCH_FRAML_ASSOCIATED_CUSTOMERS,
  FETCH_FRAML_ASSOCIATED_CUSTOMERS_SUCCESS,
  FETCH_FRAML_ASSOCIATED_CUSTOMERS_FAIL,
  FETCH_FRAML_ASSOCIATED_ACCOUNTS,
  FETCH_FRAML_ASSOCIATED_ACCOUNTS_SUCCESS,
  FETCH_FRAML_ASSOCIATED_ACCOUNTS_FAIL,
  FETCH_FRAML_SIMILAR_KYC,
  FETCH_FRAML_SIMILAR_KYC_SUCCESS,
  FETCH_FRAML_SIMILAR_KYC_FAIL
} from '../../actions/cm/framlCustomerDetail';

export const initialState = {
  aggregate: {
    data: null,
    isLoading: false,
    error: null
  },
  // FIN-12885 — per-alert Resolve (auto-assign → NO_ISSUE) progress/outcome
  resolve: {
    isLoading: false,
    error: null,
    lastResolvedAlertId: null
  },
  scoreHistory: {
    data: [],
    isLoading: false,
    error: null
  },
  alerts: {
    fraud: [],
    aml: [],
    isLoading: false,
    error: null
  },
  riskFactors: {
    data: [],
    isLoading: false,
    error: null
  },
  customerDetails: {
    data: [],
    isLoading: false,
    error: null
  },
  associatedCustomers: {
    data: [],
    isLoading: false,
    error: null
  },
  associatedAccounts: {
    data: [],
    isLoading: false,
    error: null
  },
  similarKyc: {
    data: [],
    isLoading: false,
    error: null
  }
};

// Associated customers come back as C2cResponse { relatedCustomers/list, totalCount };
// normalise to the array the table renders. Accept a few likely shapes defensively.
function toList(payload) {
  if (Array.isArray(payload)) return payload;
  if (!payload) return [];
  return payload.relatedCustomers || payload.list || payload.data || [];
}

// alert_source_domain is a STRING ENUM per v4 DDL: 'FRAUD' | 'AML'
const FRAUD_DOMAIN = 'FRAUD';
const AML_DOMAIN = 'AML';

function splitAlertsByDomain(alerts) {
  const fraud = alerts.filter((a) => a.alert_source_domain === FRAUD_DOMAIN);
  const aml = alerts.filter((a) => a.alert_source_domain === AML_DOMAIN);
  return { fraud, aml };
}

export default function framlCustomerDetailReducer(
  state = initialState,
  { type, payload }
) {
  switch (type) {
    // --- Aggregate ---
    case FETCH_FRAML_CUSTOMER_AGGREGATE:
      return {
        ...state,
        aggregate: { data: null, isLoading: true, error: null }
      };
    case FETCH_FRAML_CUSTOMER_AGGREGATE_SUCCESS:
      return {
        ...state,
        aggregate: { data: payload, isLoading: false, error: null }
      };
    case FETCH_FRAML_CUSTOMER_AGGREGATE_FAIL:
      return {
        ...state,
        aggregate: { data: null, isLoading: false, error: payload.message }
      };

    // --- Score History ---
    case FETCH_FRAML_CUSTOMER_SCORE_HISTORY:
      return {
        ...state,
        scoreHistory: { data: [], isLoading: true, error: null }
      };
    case FETCH_FRAML_CUSTOMER_SCORE_HISTORY_SUCCESS:
      return {
        ...state,
        scoreHistory: { data: payload, isLoading: false, error: null }
      };
    case FETCH_FRAML_CUSTOMER_SCORE_HISTORY_FAIL:
      return {
        ...state,
        scoreHistory: { data: [], isLoading: false, error: payload.message }
      };

    // --- Alerts ---
    case FETCH_FRAML_CUSTOMER_ALERTS:
      return {
        ...state,
        alerts: { fraud: [], aml: [], isLoading: true, error: null }
      };
    case FETCH_FRAML_CUSTOMER_ALERTS_SUCCESS: {
      const { fraud, aml } = splitAlertsByDomain(payload || []);
      return {
        ...state,
        alerts: { fraud, aml, isLoading: false, error: null }
      };
    }
    case FETCH_FRAML_CUSTOMER_ALERTS_FAIL:
      return {
        ...state,
        alerts: { fraud: [], aml: [], isLoading: false, error: payload.message }
      };

    // --- Risk Factors ---
    case FETCH_FRAML_CUSTOMER_RISK_FACTORS:
      return {
        ...state,
        riskFactors: { data: [], isLoading: true, error: null }
      };
    case FETCH_FRAML_CUSTOMER_RISK_FACTORS_SUCCESS:
      return {
        ...state,
        riskFactors: { data: payload, isLoading: false, error: null }
      };
    case FETCH_FRAML_CUSTOMER_RISK_FACTORS_FAIL:
      return {
        ...state,
        riskFactors: { data: [], isLoading: false, error: payload.message }
      };

    // --- Customer Details (party identity) ---
    case FETCH_FRAML_CUSTOMER_DETAILS:
      return {
        ...state,
        customerDetails: { data: [], isLoading: true, error: null }
      };
    case FETCH_FRAML_CUSTOMER_DETAILS_SUCCESS:
      return {
        ...state,
        customerDetails: { data: payload || [], isLoading: false, error: null }
      };
    case FETCH_FRAML_CUSTOMER_DETAILS_FAIL:
      return {
        ...state,
        customerDetails: { data: [], isLoading: false, error: payload.message }
      };

    // --- Associated Customers ---
    case FETCH_FRAML_ASSOCIATED_CUSTOMERS:
      return {
        ...state,
        associatedCustomers: { data: [], isLoading: true, error: null }
      };
    case FETCH_FRAML_ASSOCIATED_CUSTOMERS_SUCCESS:
      return {
        ...state,
        associatedCustomers: {
          data: toList(payload),
          isLoading: false,
          error: null
        }
      };
    case FETCH_FRAML_ASSOCIATED_CUSTOMERS_FAIL:
      return {
        ...state,
        associatedCustomers: {
          data: [],
          isLoading: false,
          error: payload.message
        }
      };

    // --- Associated Accounts ---
    case FETCH_FRAML_ASSOCIATED_ACCOUNTS:
      return {
        ...state,
        associatedAccounts: { data: [], isLoading: true, error: null }
      };
    case FETCH_FRAML_ASSOCIATED_ACCOUNTS_SUCCESS:
      return {
        ...state,
        associatedAccounts: {
          data: payload || [],
          isLoading: false,
          error: null
        }
      };
    case FETCH_FRAML_ASSOCIATED_ACCOUNTS_FAIL:
      return {
        ...state,
        associatedAccounts: {
          data: [],
          isLoading: false,
          error: payload.message
        }
      };

    // --- Similar KYC ---
    case FETCH_FRAML_SIMILAR_KYC:
      return {
        ...state,
        // FIN-12966 — preserve totalCount across a page change so the pagination control keeps
        // its page count while the next page loads (blanking it would collapse to one page).
        similarKyc: {
          ...state.similarKyc,
          data: [],
          isLoading: true,
          error: null
        }
      };
    case FETCH_FRAML_SIMILAR_KYC_SUCCESS:
      // FIN-12966 — the BE now returns { customers, totalCount, isTruncated }. Tolerate the old
      // bare-array shape so a stale BE degrades instead of rendering an empty grid.
      return {
        ...state,
        similarKyc: Array.isArray(payload)
          ? {
              data: payload,
              totalCount: payload.length,
              isTruncated: false,
              isLoading: false,
              error: null
            }
          : {
              data: (payload && payload.customers) || [],
              totalCount: (payload && payload.totalCount) || 0,
              isTruncated: Boolean(payload && payload.isTruncated),
              isLoading: false,
              error: null
            }
      };
    case FETCH_FRAML_SIMILAR_KYC_FAIL:
      return {
        ...state,
        similarKyc: {
          data: [],
          totalCount: 0,
          isTruncated: false,
          isLoading: false,
          error: payload.message
        }
      };

    case RESOLVE_FRAML_ALERT:
      return {
        ...state,
        resolve: { isLoading: true, error: null, lastResolvedAlertId: null }
      };
    case RESOLVE_FRAML_ALERT_SUCCESS:
      return {
        ...state,
        resolve: {
          isLoading: false,
          error: null,
          lastResolvedAlertId: payload.alertId
        }
      };
    case RESOLVE_FRAML_ALERT_FAIL:
      return {
        ...state,
        resolve: {
          isLoading: false,
          error: payload.message,
          lastResolvedAlertId: null
        }
      };
    default:
      return state;
  }
}
